// v2.19.22e-1 — Composant générique CRUD directory pour OPS B2B.
// Schema-driven : la modale + la grid sont entièrement dérivées du schema.
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

// ─── Modal d'édition schema-driven ──────────────────────────────────────
function DirectoryItemModal({ token, schema, endpoints, label, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => {
    const base = {};
    for (const f of schema) {
      const v = initial?.[f.key];
      if (f.type === "csv") base[f.key] = Array.isArray(v) ? v.join(", ") : (v || "");
      else if (f.type === "number") base[f.key] = v == null ? "" : v;
      else base[f.key] = v ?? "";
    }
    return base;
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    for (const f of schema) {
      if (f.readonly) continue;
      const val = form[f.key];
      if (f.required && (val === "" || val == null)) {
        toast.error(`Champ requis : ${f.label}`);
        return false;
      }
      if (f.maxLength && val && String(val).length > f.maxLength) {
        toast.error(`${f.label} : max ${f.maxLength} caractères`);
        return false;
      }
      if (f.type === "number" && val !== "" && val != null) {
        const n = Number(val);
        if (Number.isNaN(n)) {
          toast.error(`${f.label} doit être un nombre`);
          return false;
        }
        if (f.min != null && n < f.min) { toast.error(`${f.label} ≥ ${f.min}`); return false; }
        if (f.max != null && n > f.max) { toast.error(`${f.label} ≤ ${f.max}`); return false; }
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    const payload = {};
    for (const f of schema) {
      if (f.readonly && !isEdit) continue;
      const val = form[f.key];
      if (val === "" || val == null) continue;
      if (f.type === "csv") {
        payload[f.key] = String(val).split(",").map((s) => s.trim()).filter(Boolean);
      } else if (f.type === "number") {
        payload[f.key] = Number(val);
      } else {
        payload[f.key] = val;
      }
    }
    if (!isEdit) delete payload.id;
    try {
      const url = isEdit ? endpoints.update(initial.id) : endpoints.create;
      const r = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 503) toast.error("DB indisponible, réessaie.");
        else toast.error(data.error || `Erreur ${r.status}`);
        return;
      }
      toast.success(isEdit ? "Mis à jour" : "Créé");
      onSaved(data.item);
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="opsb2b-overlay" data-testid="dirtab-modal">
      <div className="opsb2b-modal">
        <div className="opsb2b-modal-header">
          <div className="opsb2b-modal-title">
            {isEdit ? `✏️ Éditer — ${label}` : `+ Nouveau — ${label}`}
          </div>
          <button onClick={onClose} className="opsb2b-icon-btn" data-testid="dirtab-modal-close">✕</button>
        </div>
        <div className="opsb2b-modal-body">
          {schema.map((f) => {
            if (f.readonly && !isEdit) return null;
            const val = form[f.key] ?? "";
            const id = `field-${f.key}`;
            return (
              <div key={f.key} className="opsb2b-field">
                <label htmlFor={id}>
                  {f.label}{f.required ? " *" : ""}
                </label>
                {f.type === "select" ? (
                  <select
                    id={id}
                    value={val}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="opsb2b-input"
                    disabled={f.readonly}
                    data-testid={`dirtab-field-${f.key}`}
                  >
                    <option value="">— choisir —</option>
                    {(f.options || []).map((o) => {
                      const ov = typeof o === "string" ? o : o.value;
                      const ol = typeof o === "string" ? o : o.label;
                      return <option key={ov} value={ov}>{ol}</option>;
                    })}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    id={id}
                    value={val}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="opsb2b-textarea"
                    rows={3}
                    disabled={f.readonly}
                    data-testid={`dirtab-field-${f.key}`}
                  />
                ) : (
                  <input
                    id={id}
                    type={f.type === "number" ? "number" : "text"}
                    value={val}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={f.placeholder || ""}
                    className={`opsb2b-input ${f.readonly ? "opsb2b-readonly" : ""}`}
                    readOnly={f.readonly}
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    maxLength={f.maxLength}
                    data-testid={`dirtab-field-${f.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="opsb2b-modal-footer">
          <button onClick={onClose} className="opsb2b-btn-ghost" data-testid="dirtab-modal-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} className="opsb2b-btn-primary" data-testid="dirtab-modal-save">
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Card preview ────────────────────────────────────────────────────────
function DirectoryItemCard({ item, primaryFields, schema, onEdit, onDelete, onToggle }) {
  const photo = item.photo_url || item.image_url;
  const disabled = item.enabled === false || !!item.deleted_at;
  const initials = (item.name || item.service_name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const [primary, ...rest] = primaryFields;
  const primaryVal = item[primary] || item.name || item.service_name;
  return (
    <div className={`opsb2b-card ${disabled ? "opsb2b-card-disabled" : ""}`} data-testid={`dirtab-card-${item.id}`}>
      <div className="opsb2b-card-media">
        {photo ? <img src={photo} alt={primaryVal} loading="lazy" /> : <div className="opsb2b-card-initials">{initials}</div>}
        {item.deleted_at && <div className="opsb2b-badge opsb2b-badge-critical">Supprimé</div>}
        {item.verified && <div className="opsb2b-badge opsb2b-badge-verified">✓ Vérifié</div>}
        {item.enabled === false && !item.deleted_at && <div className="opsb2b-badge opsb2b-badge-off">Désactivé</div>}
      </div>
      <div className="opsb2b-card-body">
        <div className="opsb2b-card-name">{primaryVal}</div>
        <div className="opsb2b-card-role">
          {rest.map((f) => item[f]).filter(Boolean).join(" • ") || "—"}
        </div>
        {Array.isArray(item.tags) && item.tags.length > 0 && (
          <div className="opsb2b-card-tags">
            {item.tags.slice(0, 3).map((t, i) => <span key={i} className="opsb2b-tag">{t}</span>)}
          </div>
        )}
      </div>
      <div className="opsb2b-card-actions">
        <button onClick={() => onEdit(item)} className="opsb2b-icon-btn" title="Éditer" data-testid={`dirtab-edit-${item.id}`}>✏️</button>
        <button onClick={() => onToggle(item)} className="opsb2b-icon-btn" title="Toggle enabled" data-testid={`dirtab-toggle-${item.id}`}>👁</button>
        <button onClick={() => onDelete(item)} className="opsb2b-icon-btn opsb2b-danger" title="Supprimer" data-testid={`dirtab-delete-${item.id}`}>🗑</button>
      </div>
    </div>
  );
}

// ─── Composant principal ────────────────────────────────────────────────
export default function DirectoryCrudTab({
  domain,
  label,
  emoji,
  schema,
  primaryFields,
  searchFields, // pour compat future, le filtre serveur s'en charge
  categoryField = "category_id",
  communeField = "commune",
  categoryOptions = [],
  communeOptions = [],
  token,
}) {
  const endpoints = useMemo(() => ({
    list: `${API}/api/ops-b2b/${domain}`,
    create: `${API}/api/ops-b2b/${domain}`,
    update: (id) => `${API}/api/ops-b2b/${domain}/${encodeURIComponent(id)}`,
    remove: (id) => `${API}/api/ops-b2b/${domain}/${encodeURIComponent(id)}`,
    toggleEnabled: (id) => `${API}/api/ops-b2b/${domain}/${encodeURIComponent(id)}/toggle-enabled`,
  }), [domain]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterCommune, setFilterCommune] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (filterCat) params.set("category", filterCat);
      if (filterCommune) params.set("commune", filterCommune);
      if (includeDeleted) params.set("include_deleted", "true");
      const url = `${endpoints.list}?${params.toString()}`;
      const r = await fetch(url, { headers: authHeaders(token) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      toast.error(`Impossible de charger ${label}`);
    } finally {
      setLoading(false);
    }
  }, [endpoints.list, token, search, filterCat, filterCommune, includeDeleted, label]);

  useEffect(() => { reload(); }, [reload]);

  const sorted = useMemo(() => {
    const arr = items.slice();
    arr.sort((a, b) => {
      const an = a.name || a.service_name || "";
      const bn = b.name || b.service_name || "";
      return String(an).localeCompare(String(bn));
    });
    return arr;
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleEdit = (it) => { setEditing(it); setModalOpen(true); };
  const handleCreate = () => { setEditing(null); setModalOpen(true); };

  const handleDelete = async (it) => {
    if (!window.confirm(`Supprimer "${it.name || it.service_name}" ? Soft-delete réversible via "Inclure supprimés".`)) return;
    try {
      const r = await fetch(endpoints.remove(it.id), { method: "DELETE", headers: authHeaders(token) });
      if (r.status === 204) {
        toast.success("Supprimé (soft)");
        reload();
      } else {
        const data = await r.json().catch(() => ({}));
        toast.error(data.error || `Erreur ${r.status}`);
      }
    } catch (e) { toast.error("Erreur réseau"); }
  };

  const handleToggle = async (it) => {
    try {
      const r = await fetch(endpoints.toggleEnabled(it.id), { method: "POST", headers: authHeaders(token) });
      const data = await r.json();
      if (!r.ok) { toast.error(data.error || "Erreur"); return; }
      toast.success(data.enabled ? "Activé" : "Désactivé");
      reload();
    } catch (e) { toast.error("Erreur réseau"); }
  };

  const handleSaved = () => { setModalOpen(false); setEditing(null); reload(); };

  const hasCategoryFilter = schema.some((f) => f.key === categoryField);
  const hasCommuneFilter = schema.some((f) => f.key === communeField);

  return (
    <div className="opsb2b-tab" data-testid={`dirtab-${domain}`}>
      <div className="opsb2b-tab-header">
        <div>
          <div className="opsb2b-tab-title">{emoji} {label}</div>
          <div className="opsb2b-tab-sub">
            {loading ? "Chargement…" : `${sorted.length} entrée(s)${totalPages > 1 ? ` • page ${safePage}/${totalPages}` : ""}`}
          </div>
        </div>
        <div className="opsb2b-tab-actions">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="opsb2b-input opsb2b-search"
            data-testid={`dirtab-search-${domain}`}
          />
          {hasCategoryFilter && categoryOptions.length > 0 && (
            <select
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="opsb2b-input opsb2b-search"
              data-testid={`dirtab-filter-cat-${domain}`}
            >
              <option value="">Toutes catégories</option>
              {categoryOptions.map((c) => {
                const v = typeof c === "string" ? c : c.value;
                const l = typeof c === "string" ? c : c.label;
                return <option key={v} value={v}>{l}</option>;
              })}
            </select>
          )}
          {hasCommuneFilter && communeOptions.length > 0 && (
            <select
              value={filterCommune}
              onChange={(e) => setFilterCommune(e.target.value)}
              className="opsb2b-input opsb2b-search"
              data-testid={`dirtab-filter-commune-${domain}`}
            >
              <option value="">Toutes communes</option>
              {communeOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <label className="opsb2b-checkbox" data-testid={`dirtab-include-deleted-${domain}`}>
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
            <span>Inclure supprimés</span>
          </label>
          <button onClick={handleCreate} className="opsb2b-btn-primary" data-testid={`dirtab-create-${domain}`}>+ Nouveau</button>
        </div>
      </div>
      {loading ? (
        <div className="opsb2b-empty">Chargement…</div>
      ) : paged.length === 0 ? (
        <div className="opsb2b-empty">Aucune entrée.</div>
      ) : (
        <>
          <div className="opsb2b-grid">
            {paged.map((it) => (
              <DirectoryItemCard
                key={it.id}
                item={it}
                primaryFields={primaryFields}
                schema={schema}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="opsb2b-pagination">
              <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} className="opsb2b-btn-ghost">← Préc.</button>
              <span className="opsb2b-page-info">Page {safePage}/{totalPages}</span>
              <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} className="opsb2b-btn-ghost">Suiv. →</button>
            </div>
          )}
        </>
      )}
      {modalOpen && (
        <DirectoryItemModal
          token={token}
          schema={schema}
          endpoints={endpoints}
          label={label}
          initial={editing}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
